"use client";

import { useState } from "react";
import { FloatingSticker } from "@/components/wallet/sticker-card-3d";
import { cn } from "@/lib/utils";
import { findSticker } from "@/lib/wallet/sticker-share";
import { SharedStickerSheet, type StickerOwner } from "./shared-sticker-sheet";

/**
 * A Hiive sticker riding on a post or a comment: the live 3D card, tappable
 * to see it up close and whether you have it too. The owner is always the
 * post's or comment's author — the server only lets someone attach a sticker
 * they hold.
 */
export function SharedStickerEmbed({
  stickerId,
  owner,
  viewerId,
  width = 168,
  layer,
  nested = false,
  className,
}: {
  stickerId: string;
  owner: StickerOwner | null;
  /** Defaults to the signed-in session's id. */
  viewerId?: string | null;
  width?: number;
  layer?: number;
  nested?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const sticker = findSticker(stickerId);
  if (!sticker) return null;

  return (
    <>
      <button
        type="button"
        aria-label={`Hiive sticker: ${sticker.title}. Open`}
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
        className={cn(
          "-mx-3 block w-fit rounded-3xl transition-transform duration-150 ease-out-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand active:scale-[0.98]",
          className,
        )}
      >
        <FloatingSticker
          sticker={sticker}
          width={width}
          subtitle={
            owner ? `${owner.name.split(" ")[0]}'s collection` : undefined
          }
        />
      </button>
      <SharedStickerSheet
        open={open}
        onOpenChange={setOpen}
        stickerId={stickerId}
        owner={owner}
        viewerId={viewerId}
        layer={layer}
        nested={nested}
      />
    </>
  );
}
